/**
 * The classes that make something look like a Button. A link that navigates
 * wears them itself rather than wrapping a <Button>: a button inside a link is
 * two nested controls, which is invalid HTML and announced twice.
 */

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost" | "outline";
export type ButtonSize = "xs" | "sm" | "md" | "lg";

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-brand text-white hover:bg-brand-hover focus:ring-brand disabled:opacity-50",
  secondary:
    "bg-surface-alt text-text hover:bg-border focus:ring-gray-400 disabled:opacity-50",
  danger:
    "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 disabled:opacity-50",
  ghost:
    "bg-transparent text-text hover:bg-surface-alt focus:ring-gray-400 disabled:opacity-50",
  outline:
    "bg-surface border border-border text-text hover:bg-surface-alt focus:ring-gray-400 disabled:opacity-50",
};

const sizeClasses: Record<ButtonSize, string> = {
  xs: "text-xs py-1 px-2 gap-1",
  sm: "text-sm py-1.5 px-3 gap-1.5",
  md: "text-sm py-2 px-4 gap-2",
  lg: "text-base py-2.5 px-5 gap-2",
};

export function buttonClassName({
  variant = "primary",
  size = "md",
  iconOnly = false,
  className = "",
}: { variant?: ButtonVariant; size?: ButtonSize; iconOnly?: boolean; className?: string } = {}): string {
  return [
    "inline-flex items-center justify-center rounded-md font-medium",
    "transition-colors duration-150",
    "focus:outline-none focus:ring-2 focus:ring-offset-2",
    "disabled:cursor-not-allowed",
    variantClasses[variant],
    sizeClasses[size],
    // An icon with no label is a finger's target on a phone: at least 40px
    // there, and its own size from sm up.
    iconOnly && "min-h-10 min-w-10 sm:min-h-0 sm:min-w-0",
    className,
  ]
    .filter(Boolean)
    .join(" ");
}
